"use client";

import type { Tracker } from "@sa-nu/event-log-client";
import { getBrowserLogSessionId } from "@sa-nu/logger";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";

import { consumeLoginMarker } from "./login-marker-cookie";
import { screenViewIds, screenViewKey } from "./screen-view-ids";
import type { TrackingConfig } from "./tracking-config";

/**
 * 計測の起点。root layout に 1 つ置けば、クリック・ページ表示・ログイン状態の
 * 変化がすべてここから送られる。
 *
 * SDK を動的 import しているのは、root layout の静的 import が全ページの共有
 * バンドルに入るため。計測は初期表示に不要なので hydration 後に読み込む。
 */

/** apps/webapp/analytics-manifest.json の property と揃える。 */
const ANALYTICS_PROPERTY = "webapp";

/**
 * 直前に観測した User ID。ログアウトに前の ID を載せるために持つ。タブ単位
 * (sessionStorage) だと別タブでのログアウトを拾えないので、ブラウザ単位で持つ。
 */
const LAST_USER_ID_KEY = "_sanu_last_uid";

function readLastUserId(): string | null {
  try {
    return localStorage.getItem(LAST_USER_ID_KEY);
  } catch {
    // プライベートモード等で参照できないことがある。計測を止める理由にはしない。
    return null;
  }
}

function writeLastUserId(userId: string | null): void {
  try {
    if (userId == null) localStorage.removeItem(LAST_USER_ID_KEY);
    else localStorage.setItem(LAST_USER_ID_KEY, userId);
  } catch {
    // 同上
  }
}

export function AnalyticsProvider({
  collectorOrigin,
  appVersion,
  inNativeApp,
  anonymousId,
  userId,
  consent,
  consented,
}: TrackingConfig) {
  const [tracker, setTracker] = useState<Tracker | null>(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const lastPath = useRef<string | null>(null);

  // 送信時に読む。下の page / ログインの effect より先に置き、同じ commit で新しい値にする
  const userIdRef = useRef(userId);
  useEffect(() => {
    userIdRef.current = userId;
  }, [userId]);

  // tracker はページの寿命で 1 つ。userId の変化で作り直すと SDK の instance が増えていく
  useEffect(() => {
    let stop: (() => void) | undefined;
    let cancelled = false;

    import("@sa-nu/event-log-client")
      .then(({ createTracker, startClickTracking }) => {
        if (cancelled) return;
        const created = createTracker({
          collectorOrigin,
          app: {
            version: appVersion,
            platform: "web",
            in_native_app: inNativeApp,
          },
          property: ANALYTICS_PROPERTY,
          anonymousId,
          consent: { state: consent },
          getUserId: () => userIdRef.current,
          getScreenViewId: () => screenViewIds.current(),
          getDatadogSessionId: getBrowserLogSessionId,
        });
        stop = startClickTracking({ tracker: created });
        setTracker(created);
      })
      .catch((error: unknown) => {
        // デプロイ直後に古いチャンクが消えている等。計測が止まるだけで画面には影響しない。
        // webapp にはブラウザ用の logger が無いので console に残す
        // eslint-disable-next-line no-console
        console.warn("Failed to load event-log tracking", error);
      });

    return () => {
      cancelled = true;
      stop?.();
    };
  }, [collectorOrigin, appVersion, inNativeApp, anonymousId, consent]);

  // ログインは Auth0 callback が立てた印で、ログアウトは userId が消えたことで判定する。
  useEffect(() => {
    if (tracker == null) return;
    if (!consented) {
      // 拒否されたら User ID を端末にも残さない。ログインは ID 無しで数え、ログアウトは
      // 前の ID が無いと判定できないので送らない
      writeLastUserId(null);
      if (consumeLoginMarker()) tracker.track("account.login.completed", {});
      return;
    }
    const previous = readLastUserId();
    if (userId != null && consumeLoginMarker()) {
      tracker.track("account.login.completed", {});
    } else if (userId == null && previous != null) {
      // 直前まで紐付いていた ID を付けたまま送る。付けないと誰のログアウトか
      // 分からない。送り終えてから戻さないと、載る前に消えてしまう。
      tracker.track("account.logout.completed", {}, { userId: previous });
    }
    writeLastUserId(userId ?? null);
  }, [tracker, userId, consented]);

  useEffect(() => {
    if (tracker == null) return;
    const path = screenViewKey(pathname, searchParams.toString());
    // 同じ URL で再レンダリングされても二重に送らない。
    if (lastPath.current === path) return;
    lastPath.current = path;
    // LogProvider と同じ鍵で引くので、どちらの effect が先でも同じ表示 ID になる
    screenViewIds.forView(path);
    tracker.page();
  }, [tracker, pathname, searchParams]);

  return null;
}
